import React, { useContext, useState } from 'react';
import { Box, Button, FormField, Input, Select, SpaceBetween } from '@amzn/awsui-components-react/polaris';
import CONSTANTS from 'src/utils/constants';
import { CDTSegmentType } from 'src/models/segment-cdt/SegmentCdtTypes';
import { formatSegmentType } from 'src/models/segment-cdt/SegmentTableListRow';
import { SegmentTablesContext } from 'src/components/segment-cdt/SegmentTablesContext';
import { ACTIONS } from 'src/services/segment-cdt/SegmentTablesReducer';
import { creatableSegmentOptions, isValidTableName, segmentCoaName } from 'src/components/segment-cdt/create/segmentCreateUtils';

const CADENCE_OPTIONS = [
    { value: 'MONTHLY', label: 'Monthly' },
    { value: 'QUARTERLY', label: 'Quarterly' },
    { value: 'ANNUAL', label: 'Annual' },
];

/**
 * Wizard Step 1: table name (forced `ATP_CDT_` prefix) + the segment it is built on + optional refresh
 * cadence + optional Segment Population (Custom CoA, captured only). "Next" advances to the columns step.
 */
export default function SegmentMetaStep() {
    const { state, dispatch, services } = useContext(SegmentTablesContext);
    const cw = state.createWizard;
    // Custom CoA definitions are scoped to a segment; fetch them for the chosen one (null until picked,
    // which leaves the dropdown disabled). ENTITY maps to COMPANY via segmentCoaName.
    const [customCoas, coaLoading] = services.customCOAService.getCustomCOADefinitionsForSegment(segmentCoaName(cw.segmentType));

    // The grains on offer come from the COA types the server declares, the same list MDA's data-source
    // "Segment to allocate on" is built from (MultidimensionalAllocationDataSourceSelectCOA). The hook skips
    // the fetch on an empty query and the server ignores the value, so a stable non-empty one loads it once.
    //
    // `creatableSegmentOptions` does the reconciling: that response is display strings while the contract
    // needs enum members, and it carries three entries that name no creatable grain. There is no hardcoded
    // fallback, so the picker offers nothing until the response arrives and nothing if it fails.
    //
    // Deferred until the wizard is actually open. SegmentTablesView renders the wizard unconditionally and
    // Cloudscape's Modal renders its children even when `visible` is false (it CSS-hides them rather than
    // returning early), so an ungated hook here would fetch on every visit to the tab for a dropdown nobody
    // has opened. An empty query is how this hook expresses "not yet".
    //
    // The error is captured rather than discarded, and it has to be: with no fallback, a failed fetch leaves
    // an empty list that is indistinguishable from "there are no segments", and since advancing needs a
    // chosen segment it would silently block table creation. The Select reports the error and offers a retry.
    //
    // The attempt counter is the retry mechanism. The hook refetches when its query changes and the server
    // ignores the value entirely, so bumping the counter re-runs the request without meaning anything on the
    // wire.
    const [loadAttempt, setLoadAttempt] = useState(1);
    const [coaTypes, coaTypesLoading, coaTypesError] =
        services.dataSourcesService.getCoaTypes(cw.visible ? `load-${loadAttempt}` : '');
    const fetchedOptions = creatableSegmentOptions(coaTypes);

    /**
     * The chosen grain is kept in the option list even when the fetched list does not contain it, because
     * `selectedOption` can only render an option that is present.
     *
     * This matters on Back. The wizard swaps element types between steps, so returning to step 1 remounts
     * this component with an empty list and a refetch in flight, and a draft that already carries a grain
     * would render as "Select a segment" while the request still carried the real value. Worse, once columns
     * exist the Select is disabled, so the grain could be neither seen nor re-picked while the constraint
     * text claimed it was locked in. Labelled from the COA mapping, which is where the fetched labels come
     * from too, so the wording does not change when the list lands.
     */
    const segmentOptions = cw.segmentType && !fetchedOptions.some((o) => o.value === cw.segmentType)
        ? [{ value: cw.segmentType, label: formatSegmentType(cw.segmentType) }, ...fetchedOptions]
        : fetchedOptions;

    const setMeta = (patch: any) => dispatch(ACTIONS.SET_DRAFT_META.withPayload(patch));
    const nameTouchedAndInvalid = cw.tableName !== CONSTANTS.CUSTOM_DATA_TABLE_PREFIX && !isValidTableName(cw.tableName);
    const canAdvance = isValidTableName(cw.tableName) && !!cw.segmentType;

    // The ATP_CDT_ prefix is fixed and server-enforced; the user types only the suffix. Accept an edit
    // only while it still starts with the prefix (matching the CLI CreateTableModal); a keystroke that
    // would delete into the prefix is ignored, so the prefix can never be removed or doubled.
    const onNameChange = (value: string) => {
        if (value.indexOf(CONSTANTS.CUSTOM_DATA_TABLE_PREFIX) === 0) {
            setMeta({ tableName: value });
        }
    };

    return (
        <SpaceBetween size="m">
            <Box color="text-body-secondary">
                Define the table name and the segment it is built on. Columns are added in the next step.
            </Box>
            <FormField
                label="Table name"
                description="The ATP_CDT_ prefix is fixed. Add letters, numbers, and underscores after it."
                errorText={nameTouchedAndInvalid ? 'Add letters, numbers, or underscores after the ATP_CDT_ prefix.' : undefined}
            >
                <Input
                    value={cw.tableName}
                    placeholder="ATP_CDT_2D_Royalty_Opex"
                    onChange={({ detail }) => onNameChange(detail.value)}
                />
            </FormField>
            <FormField
                label="Segment to segregate on"
                description="Each row is one value of this segment. This cannot be changed after creation."
                constraintText={cw.columns.length > 0 ? 'To change the segment, first remove the columns you have added. Each column\'s data source is tied to the current segment.' : undefined}
            >
                <Select
                    selectedOption={segmentOptions.find((o) => o.value === cw.segmentType) ?? null}
                    options={segmentOptions}
                    // Locked once columns exist: a column's GL pull filter is segment-scoped, so changing the
                    // segment would silently invalidate it. The segment is immutable after creation anyway.
                    disabled={cw.columns.length > 0}
                    placeholder="Select a segment"
                    // Load-bearing, not cosmetic: with no fallback the list is empty until the response
                    // arrives, and an empty Select would otherwise read as "there are no segments".
                    // A failure is called out as one and offers a retry, because it would otherwise block
                    // table creation with nothing on screen explaining why.
                    statusType={coaTypesError ? 'error' : coaTypesLoading ? 'loading' : 'finished'}
                    loadingText="Loading segments"
                    errorText="Could not load the segments."
                    recoveryText="Retry"
                    // Needed for the settled-but-usable-list-empty case, which is reachable: a 200 whose body
                    // omits coaTypes, or one naming only COA dimensions that are not grains. Cloudscape shows
                    // an empty dropdown with no message at all unless this is supplied.
                    empty="No segments are available to build a table on."

                    // Guarded on the error: this also fires on dropdown interactions, and a bump would
                    // refetch a list that had already arrived.
                    onLoadItems={() => {
                        if (coaTypesError) {
                            setLoadAttempt((attempt) => attempt + 1);
                        }
                    }}
                    onChange={({ detail }) => setMeta({ segmentType: detail.selectedOption.value as CDTSegmentType })}
                />
            </FormField>
            <FormField label="Refresh cadence">
                <Select
                    selectedOption={CADENCE_OPTIONS.find((o) => o.value === cw.refreshCadence) ?? null}
                    options={[
                        ...(cw.refreshCadence ? [{ value: '', label: 'None' }] : []),
                        ...CADENCE_OPTIONS,
                    ]}
                    placeholder="Select a cadence (optional)"
                    onChange={({ detail }) => setMeta({ refreshCadence: detail.selectedOption.value || undefined })}
                />
            </FormField>
            <FormField label="Segment Population (Custom CoA)" description="Optional. Restrict which segment rows exist; leave blank for the full population.">
                <Select
                    disabled={!cw.segmentType}
                    statusType={coaLoading ? 'loading' : 'finished'}
                    loadingText="Loading custom CoA definitions"
                    filteringType="auto"
                    selectedOption={
                        cw.segmentCustomCoaId
                            ? { value: cw.segmentCustomCoaId, label: (customCoas ?? []).find((d) => d.tableId === cw.segmentCustomCoaId)?.tableName ?? cw.segmentCustomCoaId }
                            : null
                    }
                    options={[
                        ...(cw.segmentCustomCoaId ? [{ value: '', label: 'None (full population)' }] : []),
                        ...(customCoas ?? []).map((d) => ({ value: d.tableId, label: d.tableName })),
                    ]}
                    placeholder={cw.segmentType ? 'Leave blank for all segments' : 'Select a segment first'}
                    onChange={({ detail }) => setMeta({ segmentCustomCoaId: detail.selectedOption.value || undefined })}
                    empty={`No custom CoA definitions for ${cw.segmentType ? segmentCoaName(cw.segmentType) : 'this segment'}`}
                />
            </FormField>
            <Box float="right">
                <SpaceBetween size="xs" direction="horizontal">
                    <Button onClick={() => dispatch(ACTIONS.CLOSE_CREATE_WIZARD)}>Cancel</Button>
                    <Button variant="primary" disabled={!canAdvance} onClick={() => dispatch(ACTIONS.SET_WIZARD_STEP.withPayload(2))}>
                        Next: Add columns
                    </Button>
                </SpaceBetween>
            </Box>
        </SpaceBetween>
    );
}
